import { useCallback, useSyncExternalStore } from 'react';

/** Spelling on/off is remembered per browser. Checking is on unless someone turns it off. */
export const SPELL_CHECK_PREFERENCE_KEY = 'managed-document:spell-check';

const listeners = new Set<() => void>();
let memoryPreference: boolean | null = null;

export function readSpellCheckPreference(): boolean {
  if (memoryPreference !== null) return memoryPreference;
  try {
    return window.localStorage.getItem(SPELL_CHECK_PREFERENCE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function writeSpellCheckPreference(enabled: boolean): void {
  try {
    window.localStorage.setItem(SPELL_CHECK_PREFERENCE_KEY, enabled ? 'on' : 'off');
    memoryPreference = null;
  } catch {
    // Storage can be unavailable (private browsing); the choice still applies to this page.
    memoryPreference = enabled;
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => { if (event.key === SPELL_CHECK_PREFERENCE_KEY) listener(); };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The remembered spelling preference, shared by every editor on the page and other tabs. */
export function useSpellCheckPreference(): [enabled: boolean, setEnabled: (enabled: boolean) => void] {
  const enabled = useSyncExternalStore(subscribe, readSpellCheckPreference, () => true);
  const setEnabled = useCallback((next: boolean) => writeSpellCheckPreference(next), []);
  return [enabled, setEnabled];
}
